import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  useEffect(() => {
    const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // 設定頁面 Meta
    document.title = '聯絡我們 | laigame手遊代儲 - 24小時全年無休客服';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '聯絡laigame手遊代儲客服團隊，提供LINE、Facebook、電話多管道聯繫方式，24小時全年無休為您服務。訂單查詢、儲值問題、帳號安全、退款申請快速處理。');
    }

    // 注入 ContactPage + LocalBusiness Schema
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          '@id': `${SITE_URL}/contact#breadcrumb`,
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': '首頁',
              'item': `${SITE_URL}/`
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': '聯絡我們',
              'item': `${SITE_URL}/contact`
            }
          ]
        },
        {
          '@type': 'ContactPage',
          '@id': `${SITE_URL}/contact#contactpage`,
          url: `${SITE_URL}/contact`,
          name: '聯絡我們 - laigame手遊代儲',
          description: '聯絡laigame手遊代儲客服團隊，24小時全年無休為您服務',
          inLanguage: 'zh-TW',
          isPartOf: {
            '@id': `${SITE_URL}/#website`
          }
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${SITE_URL}/#organization`,
          name: 'laigame手遊代儲',
          alternateName: 'laigame',
          description: '專業手遊代儲服務平台，提供原神、崩壞星穹鐵道、絕區零等熱門遊戲儲值，安全快速便宜，24小時全年無休。',
          url: `${SITE_URL}/`,
          telephone: '+886-984-189784',
          priceRange: '$$',
          currenciesAccepted: 'TWD',
          paymentAccepted: '信用卡, 銀行轉帳, LINE Pay',
          areaServed: {
            '@type': 'Country',
            'name': 'Taiwan'
          },
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'TW',
            addressRegion: '高雄市',
            addressLocality: '高雄市',
            postalCode: '800'
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 22.6273,
            longitude: 120.3014
          },
          hasMap: 'https://maps.google.com/?q=高雄市',
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday'
              ],
              opens: '00:00',
              closes: '23:59'
            }
          ],
          contactPoint: [
            {
              '@type': 'ContactPoint',
              telephone: '+886-984-189784',
              contactType: 'customer service',
              availableLanguage: ['zh-TW', 'zh-CN'],
              areaServed: 'TW',
              hoursAvailable: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
                opens: '00:00',
                closes: '23:59'
              }
            },
            {
              '@type': 'ContactPoint',
              telephone: '+886-7-9623029',
              contactType: 'customer service',
              availableLanguage: ['zh-TW', 'zh-CN'],
              areaServed: 'TW'
            }
          ],
          sameAs: [
            'https://line.me/R/ti/p/@laigame',
            'https://www.facebook.com/laigame'
          ],
          knowsAbout: [
            '手遊代儲',
            '遊戲儲值',
            '原神儲值',
            '崩壞星穹鐵道儲值',
            '絕區零儲值',
            '鳴潮儲值'
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'contact-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('contact-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const contactMethods = [
    {
      icon: 'ri-line-fill',
      title: 'LINE 官方客服',
      value: '@laigame91',
      link: 'https://line.me/R/ti/p/@laigame',
      color: 'from-green-500 to-green-600',
      description: '最快速的聯絡方式'
    },
    {
      icon: 'ri-facebook-fill',
      title: 'Facebook 粉絲團',
      value: 'laigame手遊代儲',
      link: 'https://www.facebook.com/laigame',
      color: 'from-blue-500 to-blue-600',
      description: '追蹤最新優惠資訊'
    },
    {
      icon: 'ri-phone-fill',
      title: '客服專線',
      value: '0984-189784',
      link: 'tel:+886984189784',
      color: 'from-purple-500 to-purple-600',
      description: '24小時專人服務'
    },
    {
      icon: 'ri-phone-line',
      title: '市話專線',
      value: '07-9623029',
      link: 'tel:+88679623029',
      color: 'from-indigo-500 to-indigo-600',
      description: '高雄地區專線'
    }
  ];

  const quickQuestions = [
    {
      icon: 'ri-file-list-3-line',
      title: '訂單查詢',
      description: '查詢訂單進度、儲值狀態',
      link: 'https://line.me/R/ti/p/@laigame',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: 'ri-coin-line',
      title: '儲值問題',
      description: '儲值失敗、未到帳處理',
      link: 'https://line.me/R/ti/p/@laigame',
      color: 'bg-green-50 text-green-600'
    },
    {
      icon: 'ri-shield-check-line',
      title: '帳號安全',
      description: '帳號保護、安全諮詢',
      link: 'https://line.me/R/ti/p/@laigame',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      icon: 'ri-refund-2-line',
      title: '退款申請',
      description: '退款流程、退款查詢',
      link: 'https://line.me/R/ti/p/@laigame',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-[#1a1f5e] to-[#2563eb] text-white py-20">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                聯絡我們
              </h1>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                24小時全年無休客服團隊，隨時為您解決問題
              </p>
            </div>
          </div>
        </section>

        {/* Service Time Banner */}
        <section className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-wrap items-center justify-center gap-8 text-center">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-2xl text-green-600"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">服務時間</div>
                  <div className="text-lg font-bold text-gray-900">24小時全年無休</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-customer-service-2-line text-2xl text-blue-600"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">平均回覆時間</div>
                  <div className="text-lg font-bold text-gray-900">&lt; 5 分鐘</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-purple-600"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">服務保證</div>
                  <div className="text-lg font-bold text-gray-900">專業安全可靠</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">聯絡方式</h2>
              <p className="text-gray-600">選擇您最方便的聯絡管道，我們隨時為您服務</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactMethods.map((method, index) => (
                <a
                  key={index}
                  href={method.link}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-transparent cursor-pointer"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${method.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <i className={`${method.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-2">{method.value}</p>
                  <p className="text-sm text-gray-500">{method.description}</p>
                  <div className="mt-4 flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
                    立即聯絡
                    <i className="ri-arrow-right-line ml-1"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Questions */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">快速提問</h2>
              <p className="text-gray-600">選擇您的問題類型，快速獲得協助</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickQuestions.map((question, index) => (
                <a
                  key={index}
                  href={question.link}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200 cursor-pointer"
                >
                  <div className={`w-14 h-14 ${question.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <i className={`${question.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{question.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{question.description}</p>
                  <div className="flex items-center text-blue-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
                    諮詢客服
                    <i className="ri-arrow-right-line ml-1"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Link */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-8 md:p-12 text-center border border-blue-100">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-question-answer-line text-4xl text-white"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                常見問題解答
              </h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                在聯絡客服前，您可以先查看常見問題，可能已經有您需要的答案
              </p>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#1a1f5e] to-[#2563eb] text-white rounded-full font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line text-xl"></i>
                查看常見問題
              </Link>
            </div>
          </div>
        </section>

        {/* Business Hours Detail */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-calendar-check-line text-blue-600"></i>
                服務時間說明
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">全年無休服務</h3>
                    <p className="text-gray-600">包含國定假日、春節期間，我們的客服團隊都會在線上為您服務</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">24小時即時回覆</h3>
                    <p className="text-gray-600">無論白天或深夜，我們都會在最短時間內回覆您的訊息</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">專業客服團隊</h3>
                    <p className="text-gray-600">經過專業培訓的客服人員，提供最準確、最有效的解決方案</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}